import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTrash } from 'react-icons/fa';
import useOutsideClick from '../hooks/useOutsideClick';

function FarsanItems({ allFarsanItems, FarsanRows, setFarsanRows }) {
  const { t } = useTranslation();
  const [isViewFarsanItemsList, setIsViewFarsanItemsList] = useState(false);
  const [isPopover, setIsPopover] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const searchInputRef = useRef(null);

  React.useEffect(() => {
    if (isPopover !== null && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [isPopover]);

  const unitTypeList = [
    { key: 'KGS', name: 'કિગ્રા (KGS)' },
    { key: 'Gram', name: 'ગ્રામ (Gram)' },
    { key: 'Liter', name: 'લિટર (Liter)' },
    { key: 'ML', name: 'મિલીલીટર (ML)' },
    { key: 'Pieces', name: 'નંગ (Pieces)' },
  ];

  const popoverRef = useRef(null);
  useOutsideClick(popoverRef, () => setIsPopover(null));

  const togglePopover = (index) => {
    setIsPopover(isPopover === index ? null : index);
    setSearchInput('');
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleFarsanClick = (index, farsan) => {
    setFarsanRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { ...row, farsan, unitType: farsan.unitType } : row
      )
    );
    setIsPopover(null);
    setSearchInput('');
  };

  const addNewRow = () => {
    setFarsanRows([...FarsanRows, { farsan: null, quantity: '' }]);
    setSearchInput('');
  };

  const removeRow = (index) => {
    setFarsanRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  const clearRow = (index) => {
    setFarsanRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { farsan: null, unitType: '', quantity: '' } : row
      )
    );
  };

  const isLastRowFilled = () => {
    const lastRow = FarsanRows[FarsanRows.length - 1];
    return lastRow.farsan && lastRow.unitType.trim() && lastRow.quantity.trim();
  };

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFarsanRows((prevRows) =>
      prevRows.map((row, i) => (i === index ? { ...row, [name]: value } : row))
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100">
      {/* Header */}
      <div
        className={`bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 cursor-pointer ${isViewFarsanItemsList ? 'rounded-t-2xl' : 'rounded-2xl'}`}
        onClick={() => setIsViewFarsanItemsList(!isViewFarsanItemsList)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zM3 10h18M3 14h18"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {t('FarsanItems')}
              </h3>
              <p className="text-amber-100 text-sm">
                {t('add_farsan_ingredients')}
              </p>
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsViewFarsanItemsList(!isViewFarsanItemsList);
            }}
            className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200"
          >
            <svg
              className={`w-5 h-5 text-white transform transition-transform duration-200 ${isViewFarsanItemsList ? 'rotate-180' : ''
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isViewFarsanItemsList && (
        <div className="p-6">
          <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
            {FarsanRows.map((row, index) => (
              <div key={index}>
                <div className="grid grid-cols-4 gap-4">
                  {/* Farsan Selection */}
                  <div className="relative" ref={popoverRef}>
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('FarsanItems')}
                      </label>
                    )}
                    <div
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePopover(index);
                      }}
                    >
                      <span
                        className={
                          row.farsan ? 'text-gray-900' : 'text-gray-500'
                        }
                      >
                        {row.farsan
                          ? row.farsan.name
                          : t('pls_sel_farsanitems')}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>

                    {isPopover === index && (
                      <div
                        className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto z-[9999]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3 border-b border-gray-200">
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search farsan items..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none text-sm"
                            value={searchInput}
                            onChange={handleSearchChange}
                          />
                        </div>
                        <div className="max-h-36 overflow-auto">
                          {allFarsanItems
                            ?.filter((farsan) =>
                              farsan.name
                                .toLowerCase()
                                .includes(searchInput.toLowerCase().trim())
                            )
                            .map((farsan) => (
                              <div
                                key={farsan._id}
                                className="px-3 py-2 hover:bg-amber-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                onClick={() =>
                                  handleFarsanClick(index, farsan)
                                }
                              >
                                {farsan.name}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Unit Type */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('unit_type')}
                      </label>
                    )}
                    <select
                      name="unitType"
                      value={row.unitType}
                      onChange={(e) => handleChange(index, e)}
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 appearance-none cursor-pointer"
                    >
                      <option value="">{t('select_options')}</option>
                      {unitTypeList.map((unit) => (
                        <option key={unit.key} value={unit.key}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('Quantity')}
                      </label>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      placeholder={t('Quantity')}
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50"
                      value={row.quantity}
                      onChange={(e) => {
                        const cleaned = e.target.value
                          .replace(/[^0-9.]/g, '')
                          .replace(/(\..*)\./g, '$1');
                        setFarsanRows((prevRows) =>
                          prevRows.map((r, i) =>
                            i === index ? { ...r, quantity: cleaned } : r
                          )
                        );
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-end justify-start">
                    {FarsanRows.length - 1 === index ? (
                      <div className="flex gap-2">
                        <button
                          className="px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={addNewRow}
                          disabled={!isLastRowFilled()}
                        >
                          <FaPlus className="w-4 h-4 mr-2" />
                          {t('btnAddNew')}
                        </button>
                        <button
                          className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
                          onClick={() => clearRow(index)}
                        >
                          {t('btnReset')}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center justify-center"
                        onClick={() => removeRow(index)}
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FarsanItems;
